export interface CoOwnershipCampaign {
  id: string;
  region: string;
  title: string;
  allIn: number;
  ownersWanted: number;
  ownersFilled: number;
  intendedUse: string;
  postedBy: string;
  image: string;
  type: string;
  description: string;
  proposedSplit: { label: string; value: string }[];
  steps: { step: string; title: string; desc: string }[];
  risks: string[];
}

function fmt(n: number): string {
  if (n >= 1000000) return `¥${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `¥${(n / 1000).toFixed(0)}K`;
  return `¥${n}`;
}

export const coOwnershipCampaigns: CoOwnershipCampaign[] = [
  {
    id: "CO-ODW-001",
    region: "Kanagawa · Odawara",
    title: "Odawara machiya — castle district guesthouse",
    allIn: 8500000,
    ownersWanted: 4,
    ownersFilled: 2,
    intendedUse: "Weekend guesthouse / short-term rental",
    postedBy: "NOREN Matching Board",
    image: "https://readdy.ai/api/search-image?query=Warm%20interior%20of%20a%20renovated%20Japanese%20machiya%20townhouse%20with%20open%20plan%20living%20space%20shoji%20screens%20and%20polished%20wooden%20floors%20soft%20natural%20light%20muted%20earthy%20tones%20editorial%20interior%20photography%20no%20people%20no%20text%20no%20logos&width=800&height=600&seq=noren-co-odw-001&orientation=landscape",
    type: "Kominka",
    description:
      "A machiya-style townhouse in Odawara's historic castle district, fully modelled for renovation and short-term rental operation. Co-owners would split purchase, renovation, and furnishing costs proportionally, then share access and net operating income according to a written agreement.",
    proposedSplit: [
      { label: "All-in (purchase + reno + furnish)", value: fmt(8500000) },
      { label: "Owners wanted", value: "4" },
      { label: "Share each (approx.)", value: fmt(Math.round(8500000 / 4)) },
      { label: "Spots filled", value: "2 of 4" },
      { label: "Intended use", value: "Weekend guesthouse / short-term rental" },
      { label: "Posted by", value: "NOREN Matching Board" },
    ],
    steps: [
      {
        step: "01",
        title: "Match",
        desc: "NOREN introduces verified co-owners who have passed budget and intent screening. No pooled money is held by NOREN.",
      },
      {
        step: "02",
        title: "Agreement",
        desc: "Co-owners draft and sign a written co-ownership agreement with their own lawyers and tax advisors. Terms are private between owners.",
      },
      {
        step: "03",
        title: "Purchase",
        desc: "Each co-owner buys their share (持分) directly on title in their own name. NOREN brokers the transaction as a licensed agent.",
      },
      {
        step: "04",
        title: "Registration",
        desc: "Title is registered at the Legal Affairs Bureau with each owner's share recorded. NOREN assists with documentation.",
      },
      {
        step: "05",
        title: "Optional operation",
        desc: "Co-owners may engage NOREN or a third party to manage renovation, furnishing, and rental operations. All income and costs flow directly to owners.",
      },
    ],
    risks: [
      "Co-ownership disputes must be resolved between owners. NOREN does not mediate or guarantee returns.",
      "Short-term rental licensing is location-dependent and subject to 2026 regulatory changes.",
      "Renovation costs can exceed estimates. Each owner is liable for their share of overruns.",
      "Liquidity is low. Selling a partial share in a Japanese property can take months.",
      "Foreign buyers must comply with FEFTA reporting and may face additional tax complexity.",
    ],
  },
  {
    id: "CO-YMN-002",
    region: "Kanto/Tokai · Yamanashi",
    title: "Fuji-view mountain retreat",
    allIn: 5200000,
    ownersWanted: 3,
    ownersFilled: 1,
    intendedUse: "Shared vacation home",
    postedBy: "NOREN Matching Board",
    image: "https://readdy.ai/api/search-image?query=Cozy%20interior%20of%20a%20Japanese%20mountain%20retreat%20house%20with%20large%20windows%20showing%20forest%20view%20wooden%20beams%20and%20simple%20furnishings%20soft%20warm%20light%20muted%20earth%20tones%20editorial%20interior%20photography%20no%20people%20no%20text%20no%20logos&width=800&height=600&seq=noren-co-ymn-002&orientation=landscape",
    type: "Detached House",
    description:
      "A compact mountain house with distant Mount Fuji views, ideal for a small group of co-owners seeking a shared weekend escape. Light renovation needed. Strong seasonal demand if operated as a short-term rental.",
    proposedSplit: [
      { label: "All-in (purchase + reno + furnish)", value: fmt(5200000) },
      { label: "Owners wanted", value: "3" },
      { label: "Share each (approx.)", value: fmt(Math.round(5200000 / 3)) },
      { label: "Spots filled", value: "1 of 3" },
      { label: "Intended use", value: "Shared vacation home" },
      { label: "Posted by", value: "NOREN Matching Board" },
    ],
    steps: [
      { step: "01", title: "Match", desc: "NOREN introduces verified co-owners. No money is pooled or held by NOREN." },
      { step: "02", title: "Agreement", desc: "Co-owners draft a written agreement with their own legal and tax counsel." },
      { step: "03", title: "Purchase", desc: "Each co-owner buys their share directly on title in their own name." },
      { step: "04", title: "Registration", desc: "Title registration records each owner's share at the Legal Affairs Bureau." },
      { step: "05", title: "Optional operation", desc: "Owners may engage NOREN or a third party for renovation and management." },
    ],
    risks: [
      "Mountain properties can have limited winter access and higher maintenance costs.",
      "Co-owner disputes are the owners' responsibility to resolve.",
      "Seasonal rental demand is concentrated in summer and autumn; winter occupancy may be low.",
      "Renovation scope is estimated; actual costs require on-site structural assessment.",
    ],
  },
  {
    id: "CO-HKN-003",
    region: "Kanagawa · Hakone gateway",
    title: "Hakone gateway cottage — income share",
    allIn: 18000000,
    ownersWanted: 3,
    ownersFilled: 2,
    intendedUse: "Long-term rental income",
    postedBy: "NOREN Matching Board",
    image: "https://readdy.ai/api/search-image?query=Elegant%20interior%20of%20a%20renovated%20Japanese%20cottage%20near%20hot%20springs%20with%20modern%20fixtures%20and%20warm%20wood%20tones%20soft%20natural%20light%20muted%20earthy%20palette%20editorial%20interior%20photography%20no%20people%20no%20text%20no%20logos&width=800&height=600&seq=noren-co-hkn-003&orientation=landscape",
    type: "Detached House",
    description:
      "A renovated cottage in the onsen region near Hakone, already income-producing. Co-owners would acquire the property and share long-term rental income. Premium location with year-round demand from Tokyo visitors.",
    proposedSplit: [
      { label: "All-in (purchase + minor refresh)", value: fmt(18000000) },
      { label: "Owners wanted", value: "3" },
      { label: "Share each (approx.)", value: fmt(Math.round(18000000 / 3)) },
      { label: "Spots filled", value: "2 of 3" },
      { label: "Intended use", value: "Long-term rental income" },
      { label: "Posted by", value: "NOREN Matching Board" },
    ],
    steps: [
      { step: "01", title: "Match", desc: "NOREN introduces verified co-owners. No pooled money is held." },
      { step: "02", title: "Agreement", desc: "Co-owners draft a written agreement with independent legal counsel." },
      { step: "03", title: "Purchase", desc: "Each co-owner buys their share directly on title." },
      { step: "04", title: "Registration", desc: "Title is registered with each owner's share recorded." },
      { step: "05", title: "Optional operation", desc: "NOREN can continue managing the property or owners may self-manage." },
    ],
    risks: [
      "Premium-location properties carry higher acquisition cost and lower percentage yields.",
      "Long-term tenant turnover creates vacancy risk; re-letting costs are shared.",
      "Co-owner exit requires finding a buyer for a partial share, which can take time.",
      "Hot-spring region properties may face stricter environmental and zoning rules.",
    ],
  },
  {
    id: "CO-SZK-004",
    region: "Kanto/Tokai · Shizuoka",
    title: "Shizuoka hillside kominka",
    allIn: 4800000,
    ownersWanted: 5,
    ownersFilled: 1,
    intendedUse: "Agricultural retreat / shared residence",
    postedBy: "NOREN Matching Board",
    image: "https://readdy.ai/api/search-image?query=Rustic%20interior%20of%20a%20traditional%20Japanese%20kominka%20farmhouse%20with%20exposed%20wooden%20beams%20tatami%20mats%20and%20soft%20natural%20light%20muted%20warm%20earth%20tones%20editorial%20interior%20photography%20no%20people%20no%20text%20no%20logos&width=800&height=600&seq=noren-co-szk-004&orientation=landscape",
    type: "Kominka",
    description:
      "A hillside kominka with valley views and agricultural land. Suited for a larger group of co-owners interested in a shared rural retreat or light agricultural use. Full restoration required; heritage value is high.",
    proposedSplit: [
      { label: "All-in (purchase + reno + land prep)", value: fmt(4800000) },
      { label: "Owners wanted", value: "5" },
      { label: "Share each (approx.)", value: fmt(Math.round(4800000 / 5)) },
      { label: "Spots filled", value: "1 of 5" },
      { label: "Intended use", value: "Agricultural retreat / shared residence" },
      { label: "Posted by", value: "NOREN Matching Board" },
    ],
    steps: [
      { step: "01", title: "Match", desc: "NOREN introduces verified co-owners. No pooled money is held." },
      { step: "02", title: "Agreement", desc: "Co-owners draft a written agreement with independent legal counsel." },
      { step: "03", title: "Purchase", desc: "Each co-owner buys their share directly on title." },
      { step: "04", title: "Registration", desc: "Title is registered with each owner's share recorded." },
      { step: "05", title: "Optional operation", desc: "NOREN can assist with renovation and agricultural land registration." },
    ],
    risks: [
      "Kominka restoration costs are highly variable; structural issues may not be visible until work begins.",
      "Agricultural land use requires registration with the local agricultural committee for non-farmers.",
      "Rural properties have limited resale market; partial-share liquidity is very low.",
      "With 5 co-owners, scheduling and decision-making complexity increases significantly.",
    ],
  },
  {
    id: "CO-IBR-005",
    region: "Kanto · Ibaraki",
    title: "Ibaraki apartment block — income portfolio",
    allIn: 30000000,
    ownersWanted: 4,
    ownersFilled: 3,
    intendedUse: "Income-producing rental portfolio",
    postedBy: "NOREN Matching Board",
    image: "https://readdy.ai/api/search-image?query=Exterior%20of%20a%20small%20Japanese%20apartment%20building%20in%20a%20regional%20town%20with%20clean%20lines%20and%20simple%20architecture%20overcast%20light%20muted%20warm%20earth%20tones%20documentary%20photography%20no%20people%20no%20text%20no%20logos&width=800&height=600&seq=noren-co-ibr-005&orientation=landscape",
    type: "Apartment Building",
    description:
      "A whole apartment block with 6 existing units and stable rental income. Co-owners would acquire the building and share net operating income proportionally. Professional property management recommended.",
    proposedSplit: [
      { label: "All-in (purchase + reserve fund)", value: fmt(30000000) },
      { label: "Owners wanted", value: "4" },
      { label: "Share each (approx.)", value: fmt(Math.round(30000000 / 4)) },
      { label: "Spots filled", value: "3 of 4" },
      { label: "Intended use", value: "Income-producing rental portfolio" },
      { label: "Posted by", value: "NOREN Matching Board" },
    ],
    steps: [
      { step: "01", title: "Match", desc: "NOREN introduces verified co-owners. No pooled money is held." },
      { step: "02", title: "Agreement", desc: "Co-owners draft a written agreement with independent legal counsel." },
      { step: "03", title: "Purchase", desc: "Each co-owner buys their share directly on title." },
      { step: "04", title: "Registration", desc: "Title is registered with each owner's share recorded." },
      { step: "05", title: "Optional operation", desc: "NOREN can introduce property managers or owners may self-manage." },
    ],
    risks: [
      "Multi-unit buildings carry concentrated maintenance risk; major repairs are shared costs.",
      "Tenant defaults and vacancy periods reduce income for all owners proportionally.",
      "Regional apartment yields can compress if local population declines.",
      "Co-owner exit requires a partial-share sale, which is difficult for multi-unit properties.",
    ],
  },
];

export function getCampaign(id: string): CoOwnershipCampaign | undefined {
  return coOwnershipCampaigns.find((c) => c.id === id);
}